import React, { useEffect } from "react";
import "./LegalPage.css";
import landingLogo from "@/assets/bubble-radar-landing-logo.png";

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL;

export default function PrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy — Bubble Radar";
  }, []);

  return (
    <div className="legal-page" data-testid="privacy-page">
      <header data-testid="privacy-header">
        <a className="logo" href={`${PUBLIC_SITE_URL}/`} aria-label="Bubble Radar home" data-testid="privacy-logo-link">
          <img className="logo-image" src={landingLogo} alt="Bubble Radar logo" data-testid="privacy-logo" />
        </a>
        <a className="back-link" href={`${PUBLIC_SITE_URL}/`} data-testid="privacy-back-link">
          ← Back to home
        </a>
      </header>

      <section className="hero" data-testid="privacy-hero">
        <h1 data-testid="privacy-title">Privacy Policy</h1>
        <p data-testid="privacy-effective-date">Effective date: January 26, 2026</p>
      </section>

      <section className="card" data-testid="privacy-card">
        <p data-testid="privacy-intro">
          We respect your privacy and are committed to protecting your information. This policy explains what data we collect, how we use it,
          and the choices you have. By using our services, you agree to the terms below.
        </p>

        <h2 data-testid="privacy-section-collection">Information We Collect</h2>
        <ul data-testid="privacy-collection-list">
          <li>Account details such as name, email address, and organization.</li>
          <li>Usage data including pages visited, actions taken, and signal views.</li>
          <li>Technical data like device, browser, and IP address for security and performance.</li>
        </ul>

        <h2 data-testid="privacy-section-cookies">Cookies</h2>
        <p data-testid="privacy-cookies-text">
          We use cookies and similar technologies to operate the platform, improve performance, and understand usage patterns. By using the platform,
          you agree to this use of cookies as permitted by applicable law.
        </p>

        <h2 data-testid="privacy-section-usage">How We Use Information</h2>
        <ul data-testid="privacy-usage-list">
          <li>Provide and operate the indicator platform.</li>
          <li>Improve model accuracy, stability, and user experience.</li>
          <li>Send service updates, alerts, and support communications.</li>
          <li>Protect against fraud, abuse, or unauthorized access.</li>
        </ul>

        <h2 data-testid="privacy-section-legal">Legal Basis for Processing (GDPR)</h2>
        <ul data-testid="privacy-legal-list">
          <li>Performance of a contract: to provide your account, platform access, and related services.</li>
          <li>Legitimate interests: to improve security, product quality, and service reliability.</li>
          <li>Legal obligations: to meet accounting, tax, and regulatory requirements.</li>
          <li>Consent: where required, including certain cookies and communications preferences.</li>
        </ul>

        <h2 data-testid="privacy-section-sharing">Data Sharing</h2>
        <p data-testid="privacy-sharing-text">
          We do not sell your personal data. We may share information with trusted service providers that help us operate the platform (such as analytics,
          payment processing, and messaging). These providers are bound by confidentiality and security obligations.
        </p>

        <h2 data-testid="privacy-section-transfers">International Data Transfers</h2>
        <p data-testid="privacy-transfers-text">
          If personal data is transferred outside the EEA/UK, we use appropriate safeguards as required by GDPR, such as standard contractual clauses or
          equivalent legal transfer mechanisms.
        </p>
        <p data-testid="privacy-transfers-note">Personal information may be stored on servers located in the United States.</p>

        <h2 data-testid="privacy-section-retention">Data Retention</h2>
        <p data-testid="privacy-retention-text">
          We keep data only as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce agreements.
        </p>
        <p data-testid="privacy-retention-note">
          You agree that personal information will be stored for accounting purposes according to relevant privacy laws.
        </p>

        <h2 data-testid="privacy-section-rights">Your GDPR Rights</h2>
        <ul data-testid="privacy-rights-list">
          <li>Request access to, correction of, or deletion of your personal data.</li>
          <li>Request restriction of processing or object to certain processing activities.</li>
          <li>Request data portability where applicable.</li>
          <li>Withdraw consent at any time where processing is based on consent.</li>
          <li>Opt out of non-essential communications at any time.</li>
          <li>Lodge a complaint with your local data protection supervisory authority.</li>
        </ul>

        <h2 data-testid="privacy-section-controller">Data Controller</h2>
        <p data-testid="privacy-controller-text">Bubble Radar is the data controller for personal data processed under this Privacy Policy.</p>

        <h2 data-testid="privacy-section-security">Security</h2>
        <p data-testid="privacy-security-text">
          We use industry‑standard security controls to protect data. No method of transmission is 100% secure, but we continuously monitor and improve our
          safeguards.
        </p>

        <h2 data-testid="privacy-section-contact">Contact</h2>
        <p data-testid="privacy-contact-text">
          Questions or GDPR requests? Reach out at <strong>contact@bubbleradar.com</strong>.
        </p>
      </section>

      <footer data-testid="privacy-footer">
        <span data-testid="privacy-footer-copy">© 2026 Bubble Radar. All rights reserved.</span>
      </footer>
    </div>
  );
}
